import React, { memo, useState, useEffect } from 'react';
import moment from 'moment';

import './App.css';

import { makeStyles } from '@material-ui/styles';
import { Grid, Box, Button } from '@material-ui/core';
import { LocalGroceryStoreTwoTone } from '@material-ui/icons';
import LocalMoviesIcon from '@material-ui/icons/LocalMovies';

import { initFFMPEG, mergeVideo, ILogMessage, setLog } from './core/ffmpeg';
import { editArgs, mergeArgs } from './utils/videoHandle';

export interface IProps {
    setCenterMessage: (message: string) => void;
    setIsRenderRing: (bool: boolean) => void;
    srcVideo?: string;
    srcAudio?: string;

    merge: boolean;

    audioAction: 'add' | 'remove' | 'get' | 'none';

    startTrim?: number;
    endTrim?: number;

    triggerChangeVideo: (videoData: Uint8Array) => void;
}

const useStyles = makeStyles({
    renderBox: {
        maxHeight: 250,
        overflowY: 'auto',
        borderBottom: '1px solid #f44336',
        '& ul': {
            color: '#949494',
            listStyleType: 'none',
        },
    },
});

export default memo(function Render({
    srcVideo,
    srcAudio,
    merge,
    audioAction,
    startTrim,
    endTrim,
    triggerChangeVideo,
    setCenterMessage,
    setIsRenderRing
}: IProps) {
    const classes = useStyles();
    const [trueLog, setTrueLogs] = useState<Array<string>>([]);
    const [log, setLog] = useState<string>('');
    const [isRender, setRender] = useState<boolean>(false);

    const writeLogFn = (message: ILogMessage) => {
        setLog(
            `- [${moment().format('LTS')}] ${message.type}: ${message.message}`
        );
    };

    // Check is merge
    useEffect(() => {
        if (merge) {
            handleRenderBtn();
        }
    }, [merge]);

    // Check for logs change
    useEffect(() => {
        setTrueLogs([log, ...trueLog]);
    }, [log]);

    // Start render
    const handleRenderBtn = async () => {
        setRender(true);
        setIsRenderRing(true);
        setCenterMessage('Rendering video, scroll down to see the log...');
        writeLogFn({
            type: 'INFO',
            message: 'Loading ffmpeg...',
        });

        // Init  MPEG
        const loaded = await initFFMPEG(writeLogFn).catch((err) => {
            console.log(err);
            return null;
        });
        if (loaded !== true) {
            writeLogFn({
                type: 'ERROR',
                message: 'Cannot load ffmpeg',
            });
            setRender(false);
            setIsRenderRing(false);
            setCenterMessage(
                'Cannot load ffmpeg core, scroll down to see the log output the error....'
            );
            return;
        }

        let args: Array<string> = [];

        if (merge) {
            args = mergeArgs();
        } else {
            const inputArgs: Array<string> = [];
            if (typeof srcVideo !== 'undefined')
                inputArgs.push('-i', 'srcVideo');
            if (typeof srcAudio !== 'undefined')
                inputArgs.push('-i', 'srcAudio');
            const editArguments: Array<string> = editArgs({
                audioAction,
                startTrim,
                endTrim,
            });
            args = [...inputArgs, ...editArguments];
        }

        // Start convert video
        const videoData = await mergeVideo(
            srcVideo as string,
            srcAudio as string,
            args
        ).catch((err) => {
            return null;
        });
        if (videoData === null) {
            writeLogFn({
                type: 'ERROR',
                message: 'Cannot convert video!!!',
            });
            setRender(false);
            setIsRenderRing(false);
            setCenterMessage(
                'Error while convert video, scroll down to see the log output the error....'
            );
            return;
        }
        writeLogFn({
            type: 'RESULT',
            message:
                'Convert Video Success, You Can Now Preview Your Video And Start Download!',
        });
        setRender(false);
        setIsRenderRing(false);
        setCenterMessage(
            'Convert video success, click play video below to preview and start download by pressing the button!'
        );
        triggerChangeVideo(videoData);
    };

    return (
        <Grid item xs={12}>
            <Box m={3} display='flex' justifyContent='center'>
                <Box className={classes.renderBox} display='div'>
                    <ul>
                        {trueLog.map((log, i) => (
                            <li key={i}>{log}</li>
                        ))}
                    </ul>
                </Box>
            </Box>

            <Box m={3} display='flex' justifyContent='center'>
                <Button
                    disabled={isRender}
                    variant='contained'
                    color='primary'
                    onClick={handleRenderBtn}
                >
                    {isRender ? 'Rendering....' : 'Render'}
                </Button>
            </Box>
        </Grid>
    );
});
